import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CreateQuestionConfigDto,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";

import { DOMAIN_QUESTION_DETAILS } from "../domain";

import { Error } from "@helpers";
import { questionsConfigApi } from "@api/services";
import { ACTION_GET_FORM_SECTIONS, DOMAIN_FORMS, roleGuard } from "@api";

type CreateQuestionConfigDtoProps = CreateQuestionConfigDto & {
  formSectionId: string;
};

const allowedRoles = [UserRole.Owner, UserRole.Manager];

const createQuestionConfigRequest = async (
  newQuestionConfig: CreateQuestionConfigDtoProps,
): Promise<void> => {
  try {
    await questionsConfigApi.questionsConfigControllerCreateQuestionConfig(
      newQuestionConfig,
    );
  } catch (error) {
    Error(error);
  }
};

export const useCreateQuestionConfig = (
  user: UserProfileDto | null,
  formId: string,
): {
  createQuestionConfig: (req: CreateQuestionConfigDtoProps) => void;
  createQuestionConfigAsync: (req: CreateQuestionConfigDtoProps) => Promise<void>;
  createQuestionConfigEnabled: boolean;
  isSuccessCreateQuestionConfig: boolean;
  isPendingCreateQuestionConfig: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_QUESTION_DETAILS],
    mutationFn: enabled ? createQuestionConfigRequest : undefined,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_FORMS, ACTION_GET_FORM_SECTIONS, formId],
      });
    },
  });

  return {
    createQuestionConfig: mutate,
    createQuestionConfigAsync: mutateAsync,
    createQuestionConfigEnabled: enabled,
    isSuccessCreateQuestionConfig: isSuccess,
    isPendingCreateQuestionConfig: isPending,
  };
};
